import React, { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleTheme } from '../services/themeSlice'; 
import { Classic } from '@theme-toggles/react'; 
import '@theme-toggles/react/css/Classic.css'; 
import './ChatHeader.css'
import { useGetRoomsQuery } from '../services/user';
import UserMenu from './UserMenu';

const ChatHeader = ({ referenceDoc = null, onAttachReferenceDoc, onClearReferenceDoc }) => {
  const { roomId } = useParams();
  const refDocInputRef = useRef(null);
  const { theme } = useSelector((state) => state.theme); // Get theme from Redux
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.global.authToken) || localStorage.getItem("access_token");
  const { data: roomsData } = useGetRoomsQuery(undefined, { skip: !authToken });

  const currentChatTitle = useMemo(() => {
    if (!roomId) return 'New chat';
    if (!roomsData) return 'Chat';
    const allBuckets = Object.values(roomsData || {}).flatMap((arr) => (Array.isArray(arr) ? arr : []));
    const match = allBuckets.find((r) => r?.uuid_field === roomId);
    return match?.chat_room_name || 'Chat';
  }, [roomId, roomsData]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme || 'dark');
  }, [theme]);

  const handleRefDocSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && onAttachReferenceDoc) {
      onAttachReferenceDoc(file);
    }
    e.target.value = '';
  };

  return (
    <div className="chat-header d-flex justify-content-between align-items-center">
      <div className="chat-header-left d-flex align-items-center gap-2">
        <div className="chat-title chat-header-title" title={currentChatTitle}>
          {currentChatTitle}
        </div>
        {roomId && (
          <>
            <input
              type="file"
              ref={refDocInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.xlsx,.xls,.csv,.docx"
              onChange={handleRefDocSelect}
            />
            {!referenceDoc ? (
              <button
                type="button"
                className="reference-doc-btn"
                onClick={() => refDocInputRef.current?.click()}
                title="Attach reference document to this conversation"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
                Reference doc
              </button>
            ) : (
              <div className="reference-doc-active">
                <span>📎 {referenceDoc}</span>
                <button type="button" onClick={onClearReferenceDoc} aria-label="Remove reference document">
                  ✕
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <div className="chat-header-actions d-flex align-items-center">
        <Classic
          toggled={theme === 'dark'}
          toggle={() => dispatch(toggleTheme())}
          className="theme-toggle"
          aria-label="Toggle theme"
        />
        <UserMenu />
      </div>
    </div>
  );
};

export default ChatHeader;
